import { useContext, useEffect, useRef, useState } from "react";
import { Country, State, City, ICountry, IState, ICity }  from 'country-state-city';
import Context from "../../../common/components/utils/context";
import { IAccount, IProfile } from "../../../common/models/accounts";
import { ProfileV1Service } from "../../../common/services/api/api";
import { Loading } from "../../../common/components/loading/loading";
import { Themes } from "../../../common/enums/themes";

export const AccountProfile = () => {

    const context = useContext(Context);
    const [loading, setLoading] = useState<boolean>(false)
    const [account, setAccount] = useState<IAccount>()
    const [profile, setProfile] = useState<IProfile>()

    const [country, setCountry] = useState<string>()
    const [state, setState] = useState<string>()
    const [city, setCity] = useState<string>()

    const [countries, setCountries] = useState<ICountry[]>([])
    const [states, setStates] = useState<IState[]>([])
    const [cities, setCities] = useState<ICity[]>([])

    const form = useRef(null)

    const fetchProfile = async () => {
        if (account != null && account != undefined) {
            try {
                setLoading(true);
                const profileService = new ProfileV1Service()
                var response = await profileService.get(account.accountId)
    
                setProfile(response.data)
                setLoading(false)
            } catch (error) {
                console.error("Error fetching account settings:", error)
            } finally {
                setLoading(false)
            }
        }
    }

    const updateProfile = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setLoading(true)

        try {
            const profileService = new ProfileV1Service()
            var response = await profileService.update({...profile})

            if (response.data) {
                await fetchProfile()
                setProfile(response.data)
                context.setAlert({
                    message: "Account settings updated.",
                    theme: Themes.Success
                })
            } else {
                context.setAlert({
                    message: "An error occured while updating your account settings.",
                    theme: Themes.Danger
                })
            }
        }
        catch(error) {
            console.error("Error updating account settings:", error)
        }
        finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        setAccount(context.account)
    }, [context])

    useEffect(() => {
        if (account?.accountId != undefined) {
            fetchProfile()
        }
    }, [account]);

    useEffect(() => {
        setCountries(Country.getAllCountries())
        setCountry(profile?.country)
        setState(profile?.state)
        setCity(profile?.city)
    }, [profile])

    useEffect(() => {
        setStates([])
        setCities([])
        setState('')
        setStates(State.getStatesOfCountry(country))
    }, [country])

    useEffect(() => {
        if (country && state) {
            setCities(City.getCitiesOfState(country, state))
        }
    }, [state])
    
    return loading ? <Loading /> : (
        <form
            className="overflow-hidden p-4 flex flex-col space-y-4"
            id="profileAccountForm"
            onSubmit={updateProfile}
            ref={form}
        >
            <fieldset className="border rounded-lg p-4">
                <h2 className="mb-4 font-sans font-medium">Basic Information</h2>
                <input type="hidden" name="id" defaultValue={profile?.id} required />
                <input type="hidden" name="accountId" defaultValue={profile?.accountId} required />
                <div className="columns-2">
                    <label htmlFor="firstName" className="text-gray-500 text-sm">
                        Name
                        <input
                            type="text"
                            className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
                            id="firstName"
                            name="firstName"
                            value={profile?.firstName}
                            onChange={(e) => setProfile({...profile, firstName: e.target.value})}
                        />
                    </label>
                    <label htmlFor="lastName" className="text-gray-500 text-sm">
                        Last name
                        <input
                            type="text"
                            className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
                            id="lastName"
                            name="lastName"
                            value={profile?.lastName}
                            onChange={(e) => setProfile({...profile, lastName: e.target.value})}
                        />
                    </label>
                </div>
                <label htmlFor="occupation" className="text-gray-500 text-sm">
                    Occupation
                    <input
                        type="text"
                        className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
                        id="occupation"
                        name="occupation"
                        value={profile?.occupation}
                        onChange={(e) => setProfile({...profile, occupation: e.target.value})}
                    />
                </label>
                <label htmlFor="organization" className="text-gray-500 text-sm" hidden={account?.company === null}>
                    Company/Organisation
                    <input
                        type="text"
                        className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
                        defaultValue={account?.company?.name}
                        disabled
                    />
                </label>
                <div className="columns-2">
                    <label htmlFor="country" className="text-gray-500 text-sm">
                        Country
                        <select
                            id="country" 
                            name="country"
                            className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
                            value={profile?.country ?? undefined}
                            onChange={(e) => {
                                setCountry(e.target.value)
                                setProfile({...profile, country: e.target.value})
                            }}
                        >
                            <option value="null">Select Country</option>
                            {countries.map((country, index) => 
                                <option key={index} value={country.isoCode}>
                                    {country.name}
                                </option>
                            )}
                        </select>
                    </label>
                    {states?.length || profile?.state ? (
                        <label htmlFor="state" className="text-gray-500 text-sm">
                            State/Province
                            <select
                                id="state" 
                                name="state"
                                className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
                                disabled={!country && profile?.state === null}
                                value={profile?.state ?? undefined}
                                onChange={(e) => {
                                    setState(e.target.value)
                                    setProfile({...profile, state: e.target.value})
                                }}
                            >
                                <option value="null">Select State/Province</option>
                                {states.map((state, index) => 
                                    <option key={index} value={state.isoCode}>
                                        {state.name}
                                    </option>
                                )}
                            </select>
                        </label>
                    ) : null}
                </div>
                <div className="columns-2">
                    {cities?.length || profile?.city ? (
                        <label htmlFor="city" className="text-gray-500 text-sm">
                            City
                            <select
                                id="city" 
                                name="city"
                                className="block w-full bg-gray-50 outline-none border-b py-2 px-4 mb-4"
                                disabled={!state}
                                hidden={!cities.length && profile?.city === null}
                                value={profile?.city ?? undefined}
                                onChange={(e) => {
                                    setCity(e.target.value)
                                    setProfile({...profile, city: e.target.value})
                                }}
                            >
                                <option value="null">Select City</option>
                                {cities.map((city, index) => 
                                    <option key={index} value={city.name}>
                                        {city.name}
                                    </option>
                                )}
                            </select>
                        </label>
                    ) : null}
                </div>
            </fieldset>
            <fieldset className="border rounded-lg p-4">
                <h2 className="mb-4 font-sans font-medium">Privacy and Display</h2>
                <p className="font-sans text-sm font-medium mb-3">User can see my post</p>
                <label htmlFor="privacy-public" className="text-gray-900 text-md me-4 inline-flex items-center font-sans text-sm font-normal">
                    <input
                        type="radio"
                        className="me-2 h-5 w-5"
                        id="privacy-public"
                        name="privacy"
                        checked={profile?.privacy == 0}
                        onChange={(e) => e.target.checked ? setProfile({...profile, privacy: 0}) : null}
                    />
                    <span>Public</span>
                </label>
                <label htmlFor="privacy-contacts" className="text-gray-900 text-md me-4 inline-flex items-center font-sans text-sm font-normal">
                    <input
                        type="radio"
                        className="me-2 h-5 w-5"
                        id="privacy-contacts"
                        name="privacy"
                        checked={profile?.privacy == 1}
                        onChange={(e) => e.target.checked ? setProfile({...profile, privacy: 1}) : null}
                    />
                    <span>Contacts only</span>
                </label>
                <label htmlFor="privacy-private" className="text-gray-900 text-md me-4 inline-flex items-center font-sans text-sm font-normal">
                    <input
                        type="radio"
                        className="me-2 h-5 w-5"
                        id="privacy-private"
                        name="privacy"
                        checked={profile?.privacy == 2}
                        onChange={(e) => e.target.checked ? setProfile({...profile, privacy: 2}) : null}
                    />
                    <span>Private</span>
                </label>
            </fieldset>
            <fieldset className="border rounded-lg p-4">
                <h2 className="mb-4 font-sans font-medium text-base">Notifications</h2>
                <div className="flex columns-2 mb-3">
                    <label htmlFor="pushNotifications" className="relative inline-flex cursor-pointer items-center w-full font-sans text-sm font-normal">
                        <input
                            type="checkbox"
                            className="peer sr-only"
                            id="pushNotifications"
                            name="pushNotifications"
                            checked={profile?.pushNotifications}
                            onChange={(e) => setProfile({...profile, pushNotifications: e.target.checked})}
                        />
                        <div className="peer mr-3 h-5 w-9 rounded-full border bg-gray-50 after:absolute after:left-[2px] after:top-0.5 after:h-4 after:w-4 after:rounded-full after:border after:border-gray-50 after:bg-slate-800 after:transition-all after:content-[''] peer-checked:after:bg-[#2DE1FF] peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-green-300"></div>
                        Push notifications
                    </label>
                    <label htmlFor="emailFrequencyNotification" className="relative inline-flex cursor-pointer items-center w-full font-sans text-sm font-normal">
                        <input
                            type="checkbox"
                            className="peer sr-only"
                            id="emailFrequencyNotification"
                            name="emailFrequencyNotification"
                            checked={profile?.emailFrequencyNotification}
                            onChange={(e) => setProfile({...profile, emailFrequencyNotification: e.target.checked})}
                        />
                        <div className="peer mr-3 h-5 w-9 rounded-full border bg-gray-50 after:absolute after:left-[2px] after:top-0.5 after:h-4 after:w-4 after:rounded-full after:border after:border-gray-50 after:bg-slate-800 after:transition-all after:content-[''] peer-checked:after:bg-[#2DE1FF] peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-green-300"></div>
                        Email notification frequency
                    </label>
                </div>
            </fieldset>
            <div className="grid grid-flow-col justify-end space-x-4">
                <button type="button" className="rounded-lg text-gray-400 text-sm tracking-wider font-medium outline-none bg-gray-100 hover:bg-gray-200 px-10 py-2">Cancel</button>
                <button type="submit" className="rounded-lg text-gray-800 text-sm tracking-wider font-medium outline-none bg-text-link px-10 py-2">Save</button>
            </div>
        </form>
    )
}